import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, MessageCircle, FileText, Package, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { useConfig } from "@/context/ConfigContext";

const PERKS = ["Respuesta rápida a tu solicitud", "Precios de mayoreo por volumen", "Asesoría para tu obra o taller"];
const CONTACT_OPTS = [
    { id: "whatsapp", t: "WhatsApp", icon: MessageCircle },
    { id: "llamada", t: "Llamada", icon: Phone },
    { id: "correo", t: "Correo", icon: Mail },
];

const emptyForm = { nombre: "", telefono: "", email: "", ciudad: "", tipo: "detal", detalle: "", presupuesto_ref: "", producto: "", producto_sku: "", producto_precio: "", contacto_preferencia: "whatsapp" };

export const QuoteSection = () => {
    const { hasWhatsApp, waHref } = useConfig();
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    useEffect(() => {
        const apply = () => {
            try {
                const raw = sessionStorage.getItem("lr_quote_prefill");
                if (raw) {
                    const p = JSON.parse(raw);
                    setForm((f) => ({
                        ...f,
                        detalle: p.detalle || f.detalle,
                        tipo: p.tipo || f.tipo,
                        producto: p.producto || "",
                        producto_sku: p.producto_sku || "",
                        producto_precio: p.producto_precio || "",
                    }));
                    setDone(null);
                    sessionStorage.removeItem("lr_quote_prefill");
                }
            } catch {}
        };
        apply();
        window.addEventListener("lr-quote-prefill", apply);
        return () => window.removeEventListener("lr-quote-prefill", apply);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.telefono || !form.detalle) return toast.error("Completa nombre, teléfono y el detalle");
        setLoading(true);
        try {
            const { data } = await api.post("/quotes", form);
            setDone(data);
            toast.success("¡Solicitud enviada!", { description: data.codigo });
        } catch {
            toast.error("No se pudo enviar. Intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    const wa = done ? waHref(`¡Hola ${SITE.name}! 📋 Solicité un presupuesto (${done.codigo}).\n\nCliente: ${done.nombre}\nTipo: ${done.tipo}\nDetalle: ${done.detalle}`) : null;

    const inputCls = "w-full bg-white border border-linea focus:border-madera rounded-xl outline-none py-3 px-4 text-ink transition-colors";

    return (
        <section id="presupuesto" className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                {/* Pitch */}
                <div className="lg:col-span-5">
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Sin compromiso</span>
                        <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 tracking-tight leading-tight">Solicita tu presupuesto</h2>
                        <p className="text-mut mt-4 max-w-lg leading-relaxed">
                            Cuéntanos qué materiales necesitas y te enviamos una cotización a tu medida — ideal para obras, talleres y compras al mayor.
                        </p>
                        <ul className="mt-6 space-y-3">
                            {PERKS.map((p) => (
                                <li key={p} className="flex items-center gap-3 text-ink">
                                    <CheckCircle2 size={18} className="text-verde shrink-0" /> <span className="text-sm">{p}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>

                {/* Form */}
                <div className="lg:col-span-6 lg:col-start-7">
                    <Reveal delay={0.1}>
                        <div className="bg-hueso border border-linea rounded-2xl p-6 md:p-8 shadow-lift">
                            {done ? (
                                <div className="text-center py-8" data-testid="quote-success">
                                    <CheckCircle2 size={60} className="text-verde mx-auto" strokeWidth={1.5} />
                                    <h3 className="font-display font-bold text-2xl mt-4">¡Solicitud recibida!</h3>
                                    <p className="text-mut mt-2">Tu código es <span className="font-bold text-naranja">{done.codigo}</span>. Te contactaremos muy pronto.</p>
                                    {wa && (
                                        <a href={wa} target="_blank" rel="noreferrer" data-testid="quote-whatsapp" className="mt-6 inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-3 font-semibold hover:brightness-95 transition-all">
                                            <MessageCircle size={18} /> Adelantar por WhatsApp
                                        </a>
                                    )}
                                    <button onClick={() => { setDone(null); setForm(emptyForm); }} className="block mx-auto mt-4 text-sm text-verde font-medium hover:text-naranja">
                                        Enviar otra solicitud
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-4">
                                    <div className="flex items-center gap-2 text-verde">
                                        <FileText size={18} /> <h3 className="font-display font-bold text-xl text-ink">Datos de tu solicitud</h3>
                                    </div>

                                    {form.producto && (
                                        <div className="flex items-center gap-3 bg-white border border-madera/40 rounded-xl p-3" data-testid="quote-product-chip">
                                            <div className="h-9 w-9 rounded-lg bg-madera/10 flex items-center justify-center shrink-0"><Package size={18} className="text-madera-dark" /></div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-ink line-clamp-1">{form.producto}</p>
                                                <p className="text-xs text-mut">{form.producto_sku}{form.producto_precio ? ` · ${form.producto_precio}` : ""}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <input data-testid="quote-nombre" className={inputCls} placeholder="Nombre *" value={form.nombre} onChange={set("nombre")} />
                                        <input data-testid="quote-telefono" className={inputCls} placeholder="Teléfono / WhatsApp *" value={form.telefono} onChange={set("telefono")} />
                                        <input data-testid="quote-email" className={inputCls} placeholder="Correo" value={form.email} onChange={set("email")} />
                                        <input data-testid="quote-ciudad" className={inputCls} placeholder="Ciudad" value={form.ciudad} onChange={set("ciudad")} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        {[{ id: "detal", t: "Detal" }, { id: "mayoreo", t: "Mayoreo" }].map((o) => (
                                            <button type="button" key={o.id} data-testid={`quote-tipo-${o.id}`} onClick={() => setForm((f) => ({ ...f, tipo: o.id }))} className={`py-3 rounded-xl text-sm font-semibold border transition-colors ${form.tipo === o.id ? "bg-verde text-white border-verde" : "bg-white text-ink border-linea hover:border-madera"}`}>
                                                {o.t}
                                            </button>
                                        ))}
                                    </div>
                                    <textarea data-testid="quote-detalle" className={`${inputCls} min-h-28`} placeholder="Lista de materiales o descripción de lo que necesitas… (cantidades, medidas, marcas) *" value={form.detalle} onChange={set("detalle")} />

                                    {/* Contacto preferido */}
                                    <div>
                                        <p className="text-sm font-medium text-ink mb-2">¿Cómo prefieres que te contactemos?</p>
                                        <div className="grid grid-cols-3 gap-2" data-testid="quote-contacto">
                                            {CONTACT_OPTS.map((o) => (
                                                <button type="button" key={o.id} data-testid={`quote-contacto-${o.id}`} onClick={() => setForm((f) => ({ ...f, contacto_preferencia: o.id }))} className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${form.contacto_preferencia === o.id ? "bg-verde text-white border-verde" : "bg-white text-ink border-linea hover:border-madera"}`}>
                                                    <o.icon size={15} /> {o.t}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <input data-testid="quote-presupuesto" className={inputCls} placeholder="Presupuesto estimado (opcional)" value={form.presupuesto_ref} onChange={set("presupuesto_ref")} />
                                    <button type="submit" disabled={loading} data-testid="quote-submit" className="w-full bg-naranja text-white rounded-full py-4 font-semibold hover:bg-naranja-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                                        {loading ? <><Loader2 size={16} className="animate-spin" /> Enviando…</> : "Solicitar presupuesto"}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
